import NotFoundScreen from '@/app/+not-found';
import { faqJsonLd, FaqSection } from '@/components/seo/faq-list';
import { HowItWorks } from '@/components/seo/how-it-works';
import { PhoneStage } from '@/components/seo/phone-mocks';
import {
  breadcrumbJsonLd,
  FinalCta,
  IconTiles,
  PlanLine,
  Reveal,
  SectionTitle,
  SeeAlso,
  SeoSection,
  Tagline,
} from '@/components/seo/seo-blocks';
import { SegmentDefaults } from '@/components/seo/segment-defaults';
import { SeoHero } from '@/components/seo/seo-hero';
import { SeoPageShell } from '@/components/seo/seo-page-shell';
import { ThemedText } from '@/components/themed-text';
import { SeoHead, SITE_URL } from '@/components/seo-head';
import { findComparisonPage } from '@/content/seo/comparisons';
import { GUIDE_PAGES } from '@/content/seo/guides';
import { findSegmentPage, SEGMENT_PAGES, SEGMENTS_CHECKED_AT } from '@/content/seo/segments';
import { pageTitle } from '@/lib/seo/meta';
import { Href, Redirect, useLocalSearchParams } from 'expo-router';
import React from 'react';

/** Uma página estática por ramo no export web (`/para/barbearia.html`...). */
export async function generateStaticParams(): Promise<{ segmento: string }[]> {
  return SEGMENT_PAGES.map((page) => ({ segmento: page.slug }));
}

/**
 * Página de ramo (`/para/[segmento]`): quem pesquisa "agenda para barbearia"
 * cai aqui, não na home genérica. Resposta no topo, seções em pergunta
 * próprias do ramo, os padrões que o assistente sugere (dado próprio) e o
 * FAQ, que sai do mesmo array no JSON-LD. Conteúdo em `content/seo/segments.ts`.
 */
export default function SegmentLandingScreen() {
  const { segmento } = useLocalSearchParams<{ segmento: string }>();
  const page = findSegmentPage(String(segmento ?? ''));

  // Slug que não existe é 404 de verdade, não a home.
  if (!page) return <NotFoundScreen />;
  // Apelido ou maiúscula: leva à URL canônica, uma só por ramo.
  if (page.slug !== segmento) return <Redirect href={`/para/${page.slug}` as Href} />;

  const path = `/para/${page.slug}`;
  const related = page.related
    .map((slug) => findSegmentPage(slug))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  const guides = GUIDE_PAGES.filter((guide) => guide.segments.includes(page.slug));
  const comparisons = page.comparisons
    .map((slug) => findComparisonPage(slug))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  const jsonLd = [
    faqJsonLd(page.faq),
    breadcrumbJsonLd(SITE_URL, [
      { name: 'Início', path: '/' },
      { name: page.label, path },
    ]),
  ];

  return (
    <SeoPageShell
      screenTitle={page.label}
      dockLabel={`Agenda para ${page.label.toLowerCase()}`}
      head={<SeoHead title={pageTitle(page.title)} description={page.description} path={path} jsonLd={jsonLd} />}
    >
      <SeoHero
        crumbs={[{ label: 'Início', href: '/' }, { label: page.label }]}
        meta={`Conferido com o app em ${SEGMENTS_CHECKED_AT}`}
        title={page.h1}
        sub={page.sub}
        answer={page.answer}
        badges={page.badges}
        cta
      >
        <PhoneStage mock={page.mock} />
      </SeoHero>

      <SeoSection tone="white">
        <SectionTitle eyebrow="Soa familiar?" title={page.painsTitle} center />
        <Reveal className="mx-auto mb-8 max-w-2xl">
          <ThemedText className="text-center text-base text-neutral-600 md:text-lg">{page.painsIntro}</ThemedText>
        </Reveal>
        <IconTiles items={page.pains.map((pain) => ({ icon: pain.icon, title: pain.label, sub: pain.text }))} />
      </SeoSection>

      <HowItWorks mock={page.mock} client={page.clientWord} steps={page.steps} title={page.stepsTitle} />

      <Tagline text={page.tagline} />

      <SeoSection>
        <SectionTitle eyebrow="Recursos" title={page.featuresTitle} center />
        <Reveal className="mx-auto mb-8 max-w-2xl">
          <ThemedText className="text-center text-base text-neutral-600 md:text-lg">{page.featuresIntro}</ThemedText>
        </Reveal>
        <IconTiles
          tone="card"
          items={page.features.map((feature) => ({
            icon: feature.icon,
            title: feature.title,
            sub: feature.paid ? `${feature.short} (plano pago)` : feature.short,
          }))}
        />
        <PlanLine className="mt-6" />
      </SeoSection>

      <SegmentDefaults page={page} />

      <FaqSection
        items={page.faq}
        title={page.faqTitle}
        footer={
          <SeeAlso
            title={`Guias e comparações para ${page.ramo}`}
            links={[
              ...guides.map((guide) => ({ label: guide.h1, href: `/guias/${guide.slug}` as Href })),
              ...comparisons.map((item) => ({ label: item.h1, href: `/comparar/${item.slug}` as Href })),
            ]}
            chips={[
              ...related.map((item) => ({ label: item.label, href: `/para/${item.slug}` as Href })),
              { label: 'Todos os recursos', href: '/' as Href },
              { label: 'Ver profissionais', href: '/buscar' as Href },
            ]}
          />
        }
      />

      <FinalCta title="Crie sua conta grátis e conheça" />
    </SeoPageShell>
  );
}
